import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboarding } from "@/context/OnboardingContext";

// Each step points to a DOM selector already present in the app.
// The tour navigates to the required route first, waits for the selector,
// then anchors a tooltip card next to it.
const STEPS = [
  {
    key: "dashboard",
    route: "/",
    selector: '[data-testid="ops-summary"]',
    title: "Executive Dashboard",
    body: "Every widget you need — AI briefing, 6 operational KPIs, CQC readiness, medication safety — in one place.",
    checklistKey: "visit_dashboard",
  },
  {
    key: "ops",
    route: "/",
    selector: '[data-testid="ops-summary-grid"]',
    title: "Today's Operational Summary",
    body: "Six status cards colour-coded green / amber / red. Click any card to jump straight to that module.",
  },
  {
    key: "med-safety",
    route: "/",
    selector: '[data-testid="med-safety-alert"], [data-testid="med-safety-empty"]',
    title: "Medication Safety",
    body: "Overdue doses, live countdowns and today's medication activity — all in one glance.",
  },
  {
    key: "sidebar-mar",
    route: "/",
    selector: '[data-testid="nav-mar-chart"]',
    title: "MAR Chart",
    body: "Sign every dose, refuse, withhold or PRN with a typed signature. Controlled drugs enforce a witness.",
    checklistKey: "visit_mar_chart",
  },
  {
    key: "sidebar-rota",
    route: "/",
    selector: '[data-testid="nav-rota"]',
    title: "Rota & Visits",
    body: "Assign carers to visits, use weekly templates, publish shifts and let carers accept from their phones.",
    checklistKey: "visit_rota",
  },
  {
    key: "sidebar-staff-schedule",
    route: "/",
    selector: '[data-testid="nav-staff-schedule"]',
    title: "Staff Schedule",
    body: "Drag & drop shifts between staff and days. AI Optimiser reduces clashes and travel.",
    checklistKey: "visit_staff_schedule",
  },
  {
    key: "sidebar-care-plans",
    route: "/",
    selector: '[data-testid="nav-care-plans"]',
    title: "Care Plans",
    body: "See overdue, due-soon and current plans. Generate new plans with AI using CQC-aligned templates.",
    checklistKey: "visit_care_plans",
  },
  {
    key: "sidebar-family",
    route: "/",
    selector: '[data-testid="nav-family-portal"]',
    title: "Family Portal",
    body: "Create tokenised read-only portals, message families two-way, and send a weekly digest PDF.",
    checklistKey: "visit_family_portal",
  },
  {
    key: "sidebar-audit",
    route: "/",
    selector: '[data-testid="nav-audit"]',
    title: "Audit Centre",
    body: "Every change is logged with old/new values — perfect evidence for a CQC inspection.",
    checklistKey: "visit_audit",
  },
  {
    key: "help-topbar",
    route: "/",
    selector: '[data-testid="topbar-help"]',
    title: "Help is always one click away",
    body: "Reopen this tour, browse the workflow guide or check your onboarding progress from the Help icon.",
  },
];

async function waitForSelector(selector, timeout = 4000) {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (Date.now() - start > timeout) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function computeAnchor(rect) {
  const pad = 12;
  const cardW = 320;
  const cardH = 170;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer right of the element; fall back below, then above, then centred.
  let x = rect.right + pad;
  let y = rect.top;
  if (x + cardW > vw - 12) x = Math.max(12, rect.left - cardW - pad);
  if (x < 12) x = Math.max(12, Math.min(vw - cardW - 12, rect.left));
  if (y + cardH > vh - 12) y = Math.max(12, rect.bottom - cardH);
  if (y < 12) y = 12;
  return { x, y };
}

export default function GuidedTour() {
  const { tourActive, setTourActive, mark } = useOnboarding();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [anchor, setAnchor] = useState({ x: 100, y: 100 });
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const step = STEPS[i];

  // When the tour advances, navigate then locate the selector and place the card.
  useEffect(() => {
    if (!tourActive || !step) return;
    let cancelled = false;
    setReady(false);
    (async () => {
      if (window.location.pathname !== step.route) navigate(step.route);
      // small yield so React commits
      await new Promise((r) => setTimeout(r, 60));
      const el = await waitForSelector(step.selector, 5000);
      if (cancelled) return;
      if (!el) {
        setRect(null);
        setAnchor({ x: window.innerWidth / 2 - 160, y: window.innerHeight / 2 - 80 });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // wait for scroll to settle
        await new Promise((r) => setTimeout(r, 350));
        const r = el.getBoundingClientRect();
        setRect(r);
        setAnchor(computeAnchor(r));
      }
      if (step.checklistKey) mark(step.checklistKey);
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [i, tourActive]);

  // Reset when the tour opens/closes
  useEffect(() => { if (tourActive) setI(0); }, [tourActive]);

  // Keep rect fresh on resize/scroll
  useEffect(() => {
    if (!tourActive) return;
    const onMove = () => {
      const el = step?.selector ? document.querySelector(step.selector) : null;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(r);
        setAnchor(computeAnchor(r));
      }
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [tourActive, step]);

  if (!tourActive || !step) return null;

  const finish = () => setTourActive(false);
  const next = () => (i < STEPS.length - 1 ? setI(i + 1) : finish());
  const prev = () => setI((n) => Math.max(0, n - 1));

  return createPortal(
    <div className="fixed inset-0 z-[80] pointer-events-none" data-testid="guided-tour">
      {/* Backdrop (click-through blocked) */}
      <div className="absolute inset-0 bg-slate-900/45 pointer-events-auto" onClick={finish} data-testid="guided-tour-backdrop" />

      {/* Highlight ring around target */}
      {rect && ready && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            border: "2px solid #38BDF8",
            borderRadius: 12,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.45)",
            transition: "top 200ms, left 200ms, width 200ms, height 200ms",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="fixed w-[320px] bg-white rounded-xl shadow-2xl border border-slate-200 p-4 pointer-events-auto"
        style={{ top: anchor.y, left: anchor.x, transition: "top 200ms, left 200ms" }}
        role="dialog"
        aria-label={step.title}
        data-testid="guided-tour-card"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-sky-700 font-semibold">
            <Sparkles className="w-3 h-3" /> Tour · {i + 1} / {STEPS.length}
          </div>
          <button onClick={finish} className="text-slate-400 hover:text-slate-700" data-testid="guided-tour-close" aria-label="Close tour">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="mt-1 font-heading font-semibold text-slate-900 text-base leading-tight" data-testid="guided-tour-title">
          {step.title}
        </h3>
        <p className="mt-1 text-[13px] text-slate-600 leading-snug">
          {step.body}
        </p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" onClick={prev} disabled={i === 0} data-testid="guided-tour-prev">
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            {i + 1} of {STEPS.length}
          </div>
          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={next} data-testid="guided-tour-next">
            {i === STEPS.length - 1 ? "Finish" : "Next"} <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
